import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Plus, Search, Edit2, Trash2, Building, Phone, Mail, MapPin, User } from "lucide-react";
import axios from "axios";
import ImportExportButtons from "../../components/ImportExportButtons";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SuppliersTab() {
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingSupplier, setEditingSupplier] = useState(null);
  const [newSupplier, setNewSupplier] = useState({
    name: "", contact_person: "", phone: "", email: "", address: "", notes: ""
  });

  useEffect(() => {
    fetchSuppliers();
  }, []);

  const fetchSuppliers = async () => {
    try {
      const res = await axios.get(`${API}/suppliers`);
      setSuppliers(res.data || []);
    } catch (error) {
      console.error("Error fetching suppliers:", error);
      toast.error("Failed to load suppliers");
    } finally {
      setLoading(false);
    }
  };

  const filteredSuppliers = suppliers.filter(s =>
    s.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    s.contact_person?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    s.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/admin/suppliers`, newSupplier);
      toast.success("Supplier created successfully!");
      setShowAddModal(false);
      setNewSupplier({ name: "", contact_person: "", phone: "", email: "", address: "", notes: "" });
      fetchSuppliers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create supplier");
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    try {
      await axios.patch(`${API}/admin/suppliers/${editingSupplier.id}`, {
        name: editingSupplier.name,
        contact_person: editingSupplier.contact_person,
        phone: editingSupplier.phone,
        email: editingSupplier.email,
        address: editingSupplier.address,
        notes: editingSupplier.notes
      });
      toast.success("Supplier updated successfully!");
      setEditingSupplier(null);
      fetchSuppliers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update supplier");
    }
  };

  const handleDelete = async (supplierId) => {
    if (!window.confirm("Are you sure you want to delete this supplier?")) return;
    try {
      await axios.delete(`${API}/admin/suppliers/${supplierId}`);
      toast.success("Supplier deleted successfully!");
      fetchSuppliers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete supplier");
    }
  };

  return (
    <div className="space-y-6 p-4">
      {/* Header */}
      <div className="flex flex-col gap-4">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h2 className="text-xl sm:text-2xl font-bold text-white flex items-center gap-2">
              <Building className="w-5 h-5 sm:w-6 sm:h-6 text-teal-500" />
              Suppliers
            </h2>
            <p className="text-white/60 text-sm mt-1">{suppliers.length} total suppliers</p>
          </div>
          <div className="flex flex-wrap gap-2 w-full sm:w-auto">
            <ImportExportButtons
              entityName="Suppliers"
              exportEndpoint="/admin/suppliers/export"
              importEndpoint="/admin/suppliers/import"
              templateEndpoint="/admin/suppliers/template"
              onImportSuccess={fetchSuppliers}
              exportFileName="suppliers"
            />
            <button
              onClick={() => setShowAddModal(true)}
              className="flex items-center justify-center gap-2 px-4 py-2 bg-teal-500 text-white rounded-lg hover:bg-teal-600 transition-colors whitespace-nowrap flex-1 sm:flex-none"
              data-testid="add-supplier-btn"
            >
              <Plus className="w-4 h-4" />
              <span>Add Supplier</span>
            </button>
          </div>
        </div>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" />
        <input
          type="text"
          placeholder="Search suppliers..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full pl-10 pr-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-teal-500"
          data-testid="supplier-search"
        />
      </div>

      {/* Suppliers Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filteredSuppliers.map((supplier) => (
          <div key={supplier.id} className="bg-white/5 rounded-xl p-4 border border-white/10 hover:border-teal-500/30 transition-colors">
            <div className="flex justify-between items-start mb-3">
              <h3 className="text-lg font-semibold text-white">{supplier.name}</h3>
              <div className="flex gap-1">
                <button
                  onClick={() => setEditingSupplier({ ...supplier })}
                  className="p-2 text-blue-400 hover:bg-blue-500/20 rounded-lg transition-colors"
                  data-testid={`edit-supplier-${supplier.id}`}
                >
                  <Edit2 className="w-4 h-4" />
                </button>
                <button
                  onClick={() => handleDelete(supplier.id)}
                  className="p-2 text-red-400 hover:bg-red-500/20 rounded-lg transition-colors"
                  data-testid={`delete-supplier-${supplier.id}`}
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
            <div className="space-y-2 text-sm">
              {supplier.contact_person && (
                <div className="flex items-center gap-2 text-white/70">
                  <User className="w-4 h-4 text-white/40" />
                  {supplier.contact_person}
                </div>
              )}
              {supplier.phone && (
                <div className="flex items-center gap-2 text-white/70">
                  <Phone className="w-4 h-4 text-white/40" />
                  {supplier.phone}
                </div>
              )}
              {supplier.email && (
                <div className="flex items-center gap-2 text-white/70">
                  <Mail className="w-4 h-4 text-white/40" />
                  {supplier.email}
                </div>
              )}
              {supplier.address && (
                <div className="flex items-center gap-2 text-white/70">
                  <MapPin className="w-4 h-4 text-white/40" />
                  <span className="line-clamp-2">{supplier.address}</span>
                </div>
              )}
            </div>
            {supplier.notes && (
              <p className="mt-3 pt-3 border-t border-white/10 text-white/50 text-sm line-clamp-2">
                {supplier.notes}
              </p>
            )}
          </div>
        ))}
        {filteredSuppliers.length === 0 && (
          <div className="col-span-full bg-white/5 rounded-xl p-8 text-center text-white/40">
            No suppliers found
          </div>
        )}
      </div>

      {/* Add Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-zinc-900 rounded-xl p-6 w-full max-w-lg border border-white/10 max-h-[90vh] overflow-y-auto">
            <h3 className="text-xl font-bold text-white mb-4">Add Supplier</h3>
            <form onSubmit={handleCreate} className="space-y-4">
              <div>
                <label className="block text-white/60 text-sm mb-1">Supplier Name *</label>
                <input
                  type="text"
                  value={newSupplier.name}
                  onChange={(e) => setNewSupplier({ ...newSupplier, name: e.target.value })}
                  className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-teal-500"
                  required
                  data-testid="new-supplier-name"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-white/60 text-sm mb-1">Contact Person</label>
                  <input
                    type="text"
                    value={newSupplier.contact_person}
                    onChange={(e) => setNewSupplier({ ...newSupplier, contact_person: e.target.value })}
                    className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-teal-500"
                    data-testid="new-supplier-contact"
                  />
                </div>
                <div>
                  <label className="block text-white/60 text-sm mb-1">Phone</label>
                  <input
                    type="text"
                    value={newSupplier.phone}
                    onChange={(e) => setNewSupplier({ ...newSupplier, phone: e.target.value })}
                    className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-teal-500"
                    data-testid="new-supplier-phone"
                  />
                </div>
              </div>
              <div>
                <label className="block text-white/60 text-sm mb-1">Email</label>
                <input
                  type="email"
                  value={newSupplier.email}
                  onChange={(e) => setNewSupplier({ ...newSupplier, email: e.target.value })}
                  className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-teal-500"
                  data-testid="new-supplier-email"
                />
              </div>
              <div>
                <label className="block text-white/60 text-sm mb-1">Address</label>
                <textarea
                  value={newSupplier.address}
                  onChange={(e) => setNewSupplier({ ...newSupplier, address: e.target.value })}
                  className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-teal-500"
                  rows={2}
                  data-testid="new-supplier-address"
                />
              </div>
              <div>
                <label className="block text-white/60 text-sm mb-1">Notes</label>
                <textarea
                  value={newSupplier.notes}
                  onChange={(e) => setNewSupplier({ ...newSupplier, notes: e.target.value })}
                  className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-teal-500"
                  rows={2}
                  data-testid="new-supplier-notes"
                />
              </div>
              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowAddModal(false)}
                  className="flex-1 px-4 py-2 bg-white/10 text-white rounded-lg hover:bg-white/20 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-teal-500 text-white rounded-lg hover:bg-teal-600 transition-colors"
                  data-testid="submit-new-supplier"
                >
                  Create
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {editingSupplier && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-zinc-900 rounded-xl p-6 w-full max-w-lg border border-white/10 max-h-[90vh] overflow-y-auto">
            <h3 className="text-xl font-bold text-white mb-4">Edit Supplier</h3>
            <form onSubmit={handleUpdate} className="space-y-4">
              <div>
                <label className="block text-white/60 text-sm mb-1">Supplier Name *</label>
                <input
                  type="text"
                  value={editingSupplier.name}
                  onChange={(e) => setEditingSupplier({ ...editingSupplier, name: e.target.value })}
                  className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-teal-500"
                  required
                  data-testid="edit-supplier-name"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-white/60 text-sm mb-1">Contact Person</label>
                  <input
                    type="text"
                    value={editingSupplier.contact_person || ""}
                    onChange={(e) => setEditingSupplier({ ...editingSupplier, contact_person: e.target.value })}
                    className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-teal-500"
                    data-testid="edit-supplier-contact"
                  />
                </div>
                <div>
                  <label className="block text-white/60 text-sm mb-1">Phone</label>
                  <input
                    type="text"
                    value={editingSupplier.phone || ""}
                    onChange={(e) => setEditingSupplier({ ...editingSupplier, phone: e.target.value })}
                    className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-teal-500"
                    data-testid="edit-supplier-phone"
                  />
                </div>
              </div>
              <div>
                <label className="block text-white/60 text-sm mb-1">Email</label>
                <input
                  type="email"
                  value={editingSupplier.email || ""}
                  onChange={(e) => setEditingSupplier({ ...editingSupplier, email: e.target.value })}
                  className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-teal-500"
                  data-testid="edit-supplier-email"
                />
              </div>
              <div>
                <label className="block text-white/60 text-sm mb-1">Address</label>
                <textarea
                  value={editingSupplier.address || ""}
                  onChange={(e) => setEditingSupplier({ ...editingSupplier, address: e.target.value })}
                  className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-teal-500"
                  rows={2}
                  data-testid="edit-supplier-address"
                />
              </div>
              <div>
                <label className="block text-white/60 text-sm mb-1">Notes</label>
                <textarea
                  value={editingSupplier.notes || ""}
                  onChange={(e) => setEditingSupplier({ ...editingSupplier, notes: e.target.value })}
                  className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-teal-500"
                  rows={2}
                  data-testid="edit-supplier-notes"
                />
              </div>
              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setEditingSupplier(null)}
                  className="flex-1 px-4 py-2 bg-white/10 text-white rounded-lg hover:bg-white/20 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-teal-500 text-white rounded-lg hover:bg-teal-600 transition-colors"
                  data-testid="submit-edit-supplier"
                >
                  Update
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
